import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, AlertTriangle, ShieldOff } from "lucide-react";

/**
 * /settings/delete-account — DPDP / GDPR self-serve erasure.
 * Explicit consent + confirmation phrase + password + step-up token
 * are all required. The preview API tells the user exactly how many
 * rows will disappear before they commit.
 */
export default function DeleteAccount() {
    const nav = useNavigate();
    const [preview, setPreview] = useState(null);
    const [consent, setConsent] = useState(false);
    const [confirmation, setConfirmation] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);

    const load = async () => {
        try {
            const r = await api.get("/api/privacy/erasure/preview");
            setPreview(r.data);
        } catch (e) {
            if (e?.response?.status === 401 || e?.response?.status === 403) {
                toast.error("Please complete step-up authentication first");
                nav("/security");
                return;
            }
            toast.error("Couldn't load erasure preview");
        }
    };

    useEffect(() => { load(); }, []);

    const canRequest = consent && confirmation === "DELETE MY VAULT";
    const canExecute = canRequest && password.length >= 1;

    const request = async () => {
        setBusy(true);
        try {
            await api.post("/api/privacy/erasure/request",
                { consent, confirmation });
            toast.success("Erasure request recorded. Complete the final step to execute.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Request failed");
        } finally { setBusy(false); }
    };

    const execute = async () => {
        if (!window.confirm("This is permanent. Continue?")) return;
        setBusy(true);
        try {
            const r = await api.post("/api/privacy/erasure/execute",
                { consent, confirmation, password });
            setDone(r.data);
            toast.success("Your VAULT has been erased.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Execute failed");
        } finally { setBusy(false); }
    };

    if (done) {
        return (
            <div data-testid="delete-account-done" className="min-h-screen bg-[#0b1e3f] text-[#f6f3ec] flex items-center justify-center p-6">
                <div className="max-w-md text-center">
                    <ShieldOff size={40} className="mx-auto mb-4 text-[#c9a253]" />
                    <h1 className="font-['Fraunces',serif] text-3xl mb-2">Your VAULT has been erased.</h1>
                    <p className="text-sm opacity-80">
                        {done.total_deleted} row(s) across {Object.keys(done.per_collection || {}).length} collections were permanently deleted.
                        Anonymised audit metadata remains to demonstrate compliance.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="delete-account" className="min-h-screen bg-[#f6f3ec] text-[#0b1e3f]">
            <div className="max-w-2xl mx-auto p-6">
                <div className="flex items-center gap-3 mb-6">
                    <Link to="/security" className="text-stone-500 hover:text-stone-800"><ArrowLeft size={18} /></Link>
                    <div>
                        <div className="text-[10.5px] uppercase tracking-widest text-[#a0894a]">Privacy &amp; data</div>
                        <h1 className="font-['Fraunces',serif] text-2xl">Delete my VAULT</h1>
                    </div>
                </div>

                <Card className="mb-4 border-rose-200 bg-rose-50/40">
                    <CardHeader>
                        <CardTitle className="text-base font-['Fraunces',serif] text-rose-800 flex items-center gap-2">
                            <AlertTriangle size={16} /> This is permanent
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-rose-800 space-y-2">
                        <p>Erasure removes cards, expenses, statements, documents, subscriptions, cash accounts, milestones and any personal identifiers linked to your VAULT.</p>
                        <p>Anonymised audit metadata (e.g. hashed user id in the consent log) may be retained solely to demonstrate compliance with DPDP / GDPR.</p>
                        <p>Transactional email delivery is stopped immediately. Marketing suppression is applied on your email address for good.</p>
                    </CardContent>
                </Card>

                {preview && (
                    <Card className="mb-4" data-testid="delete-preview">
                        <CardHeader>
                            <CardTitle className="text-base font-['Fraunces',serif]">
                                What will be deleted ({preview.total_rows} rows)
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                                {Object.entries(preview.counts_per_collection)
                                    .filter(([, n]) => n > 0)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([name, n]) => (
                                        <div key={name} className="flex justify-between border border-stone-200 bg-white rounded px-2 py-1">
                                            <span className="text-stone-600">{name}</span>
                                            <span className="font-semibold">{n}</span>
                                        </div>
                                    ))}
                                {preview.total_rows === 0 && (
                                    <div className="col-span-full text-stone-500">Nothing to delete — the vault is already empty.</div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                )}

                <Card>
                    <CardHeader><CardTitle className="text-base font-['Fraunces',serif]">Confirm erasure</CardTitle></CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        <label className="flex items-start gap-2">
                            <input data-testid="delete-consent-check" type="checkbox"
                                checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-1" />
                            <span className="text-stone-700">{preview?.consent_text || "I understand this is permanent."}</span>
                        </label>
                        <div>
                            <div className="text-xs text-stone-500 mb-1">Type <code className="text-xs bg-stone-100 px-1 py-0.5 rounded">DELETE MY VAULT</code> to confirm</div>
                            <Input data-testid="delete-confirmation-input" placeholder="DELETE MY VAULT"
                                value={confirmation} onChange={e => setConfirmation(e.target.value)} />
                        </div>
                        <div>
                            <div className="text-xs text-stone-500 mb-1">Your VAULT password</div>
                            <Input data-testid="delete-password-input" type="password" value={password}
                                onChange={e => setPassword(e.target.value)} />
                        </div>
                        <div className="flex gap-2 pt-2">
                            <Button data-testid="delete-request-btn" variant="outline" disabled={!canRequest || busy}
                                onClick={request}>Record request</Button>
                            <Button data-testid="delete-execute-btn" disabled={!canExecute || busy}
                                onClick={execute}
                                className="bg-rose-600 hover:bg-rose-700 text-white">
                                Erase my VAULT permanently
                            </Button>
                        </div>
                        <div className="text-[11px] text-stone-500 pt-2">
                            Erasure request &amp; execution are recorded (hashed) in your consent log for DPDP / GDPR compliance.
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
